import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en/translation.json';
import hi from './locales/hi/translation.json';
import te from './locales/te/translation.json';
import listingQuestionsEn from './locales/listing_questions_en.json';
import listingQuestionsHi from './locales/listing_questions_hi.json';
import listingQuestionsTe from './locales/listing_questions_te.json';
import searchQuestionsEn from './locales/search_questions_en.json';
import searchQuestionsHi from './locales/search_questions_hi.json';
import searchQuestionsTe from './locales/search_questions_te.json';

const syncDocumentLanguage = (lng: string) => {
    const base = (lng || 'en').split('-')[0];
    document.documentElement.lang = base;
};

/** Locale files sometimes store "" — treat as missing and fall back to English or interpolated defaultValue. */
const emptyStringFallback = {
    type: 'postProcessor' as const,
    name: 'emptyStringFallback',
    process(
        value: string,
        key: string,
        options: Record<string, unknown>,
        translator: { translate: (key: string, opts?: Record<string, unknown>) => string },
    ) {
        if (value !== '') return value;
        const lng = String(options.lng ?? i18n.language ?? 'en').split('-')[0];
        if (lng !== 'en') {
            const safeOptions: Record<string, unknown> = { ...options, lng: 'en', postProcess: [] };
            // Avoid EN template + non-English {{property}} (and similar) hybrids
            // when a locale key is missing and we fall back to English copy.
            for (const interpolKey of ['property', 'sharingLabel']) {
                const v = safeOptions[interpolKey];
                if (typeof v === 'string' && /[^\x00-\x7F]/.test(v)) {
                    if (interpolKey === 'property') safeOptions.property = 'property';
                    else delete safeOptions[interpolKey];
                }
            }
            const enValue = translator.translate(key, safeOptions);
            if (enValue !== '' && enValue !== key) return enValue;
        }
        if (typeof options.defaultValue === 'string' && options.defaultValue) {
            let res = options.defaultValue;
            for (const [k, v] of Object.entries(options)) {
                if (typeof v === 'string' || typeof v === 'number') {
                    res = res.replaceAll(`{{${k}}}`, String(v));
                }
            }
            return res;
        }
        return value;
    },
};

i18n
    .use(emptyStringFallback)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: {
            en: {
                translation: {
                    ...en,
                    listing_questions: listingQuestionsEn,
                    search_questions: searchQuestionsEn,
                }
            },
            hi: {
                translation: {
                    ...hi,
                    listing_questions: listingQuestionsHi,
                    search_questions: searchQuestionsHi,
                }
            },
            te: {
                translation: {
                    ...te,
                    listing_questions: listingQuestionsTe,
                    search_questions: searchQuestionsTe,
                }
            }
        },
        fallbackLng: 'en',
        supportedLngs: ['en', 'hi', 'te'],
        interpolation: {
            escapeValue: false // react already safes from xss
        },
        detection: {
            order: ['localStorage', 'cookie', 'navigator'],
            caches: ['localStorage', 'cookie'],
            lookupCookie: 'lmi_preferred_language', // Matches the backend field name
            lookupLocalStorage: 'lmi_i18nextLng',
            cookieOptions: { path: '/', maxAge: 31536000 } // 1 year
        },
        load: 'languageOnly',
        postProcess: ['emptyStringFallback'],
    });

i18n.on('languageChanged', syncDocumentLanguage);
i18n.on('initialized', () => syncDocumentLanguage(i18n.language));

export default i18n;
